/**
 * dataIntegrityService.js
 * 
 * Service for checking and maintaining data integrity in the inventory system.
 * Provides tools to detect and fix inconsistencies in product and warehouse data.
 */

import stockService from './stockService';

class DataIntegrityService {
  /**
   * Check integrity of all products
   * @returns {Object} Results of integrity check
   */
  checkAllProducts() {
    try {
      console.group('Data Integrity Check: All Products');
      
      const products = this._loadProducts();
      const results = {
        totalProducts: products.length,
        validProducts: 0,
        invalidProducts: 0,
        issues: [],
        productIssues: []
      };
      
      for (const product of products) {
        const checkResult = stockService.verifyProductIntegrity(product.id);
        
        if (checkResult.valid) {
          results.validProducts++;
        } else {
          results.invalidProducts++;
          results.productIssues.push({
            productId: product.id,
            productName: product.name,
            issues: checkResult.issues
          });
          
          // Add all issues to the main issues array
          results.issues = [...results.issues, ...checkResult.issues.map(issue => 
            `Product ${product.id} (${product.name}): ${issue}`
          )];
        }
      }
      
      console.log('Data Integrity Check Results:', results);
      return results;
    } catch (error) {
      console.error('Error checking data integrity:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }
  
  /**
   * Fix integrity issues for all products
   * @returns {Object} Results of fix operation
   */
  fixAllProducts() {
    try {
      console.group('Data Integrity Fix: All Products');
      
      const products = this._loadProducts();
      const results = {
        totalProducts: products.length,
        fixedProducts: 0,
        failedProducts: 0,
        fixedIssues: []
      };
      
      for (const product of products) {
        try {
          const fixed = this._fixProductIntegrity(product);
          
          if (fixed.issuesFixed > 0) {
            results.fixedProducts++;
            results.fixedIssues = [...results.fixedIssues, ...fixed.fixes.map(fix => 
              `Product ${product.id} (${product.name}): ${fix}`
            )];
          }
        } catch (productError) {
          console.error(`Error fixing product ${product.id}:`, productError);
          results.failedProducts++;
        }
      }
      
      console.log('Data Integrity Fix Results:', results);
      return results;
    } catch (error) {
      console.error('Error fixing data integrity:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }
  
  /**
   * Fix integrity issues for a specific product
   * @param {Object} product - Product to fix
   * @returns {Object} Results of fix operation
   */
  _fixProductIntegrity(product) {
    const result = {
      productId: product.id,
      issuesFixed: 0,
      fixes: []
    };
    
    // Make sure warehouseIds and warehouseStock are initialized
    if (!product.warehouseIds) {
      product.warehouseIds = [];
    }
    
    if (!product.warehouseStock) {
      product.warehouseStock = {};
    }
    
    // Ensure all warehouseIds are strings
    product.warehouseIds = product.warehouseIds.map(id => String(id));
    
    // Fix warehouseStock entries without corresponding warehouseIds
    for (const warehouseId of Object.keys(product.warehouseStock)) {
      const warehouseIdStr = String(warehouseId);
      
      // Remove numeric key if string key exists
      if (!isNaN(parseInt(warehouseId)) && 
          product.warehouseStock[warehouseIdStr] !== undefined && 
          warehouseId !== warehouseIdStr) {
        delete product.warehouseStock[warehouseId];
        result.issuesFixed++;
        result.fixes.push(`Removed duplicate numeric warehouse ID: ${warehouseId}`);
      }
      
      // Add missing warehouseId to warehouseIds array
      if (!product.warehouseIds.includes(warehouseIdStr)) {
        product.warehouseIds.push(warehouseIdStr);
        result.issuesFixed++;
        result.fixes.push(`Added missing warehouse ID to warehouseIds: ${warehouseIdStr}`);
      }
    }
    
    // Remove warehouseIds that don't have corresponding warehouseStock entries
    const validWarehouseIds = product.warehouseIds.filter(id => {
      const idStr = String(id);
      if (product.warehouseStock[idStr] === undefined) {
        result.issuesFixed++;
        result.fixes.push(`Removed warehouse ID without stock entry: ${idStr}`);
        return false;
      }
      return true;
    });
    
    product.warehouseIds = validWarehouseIds;
    
    // Ensure all stock values are numbers
    for (const warehouseId of Object.keys(product.warehouseStock)) {
      const stockValue = product.warehouseStock[warehouseId];
      if (isNaN(parseInt(stockValue))) {
        product.warehouseStock[warehouseId] = 0;
        result.issuesFixed++;
        result.fixes.push(`Fixed invalid stock value for warehouse ${warehouseId}`);
      } else {
        product.warehouseStock[warehouseId] = parseInt(stockValue);
      }
    }
    
    // Calculate total stock from warehouse stocks
    const calculatedStock = Object.values(product.warehouseStock).reduce(
      (sum, stock) => sum + parseInt(stock || 0), 
      0
    );
    
    // Fix general stock if it doesn't match warehouse stocks
    if (parseInt(product.stock || 0) !== calculatedStock) {
      product.stock = calculatedStock;
      result.issuesFixed++;
      result.fixes.push(`Updated general stock to match warehouse stocks: ${calculatedStock}`);
    }
    
    // Save the fixed product if any issues were fixed
    if (result.issuesFixed > 0) {
      this._saveProduct(product);
    }
    
    return result;
  }
  
  /**
   * Schedule periodic integrity checks
   * @param {number} intervalMinutes - Minutes between checks
   */
  schedulePeriodicChecks(intervalMinutes = 60) {
    // Clear any existing interval
    if (this._checkInterval) {
      clearInterval(this._checkInterval);
    }
    
    const intervalMs = intervalMinutes * 60 * 1000;
    
    this._checkInterval = setInterval(() => {
      console.log(`Running scheduled data integrity check (${new Date().toISOString()})`);
      const checkResults = this.checkAllProducts();
      
      // Auto-fix if issues are found
      if (checkResults.invalidProducts > 0) {
        console.log(`Found ${checkResults.invalidProducts} products with issues. Auto-fixing...`);
        this.fixAllProducts();
      }
    }, intervalMs);
    
    console.log(`Scheduled data integrity checks every ${intervalMinutes} minutes`);
    return true;
  }
  
  /**
   * Stop scheduled integrity checks
   */
  stopPeriodicChecks() {
    if (this._checkInterval) {
      clearInterval(this._checkInterval);
      this._checkInterval = null;
      console.log('Stopped periodic data integrity checks');
      return true;
    }
    return false;
  }
  
  /**
   * Load products from localStorage
   * @returns {Array} Products array
   */
  _loadProducts() {
    try {
      const products = localStorage.getItem('products') ? 
        JSON.parse(localStorage.getItem('products')) : [];
      return products;
    } catch (error) {
      console.error('Error loading products from localStorage', error);
      return [];
    }
  }
  
  /**
   * Save a product to localStorage
   * @param {Object} product - Product to save
   */
  _saveProduct(product) {
    try {
      const products = this._loadProducts();
      const productIndex = products.findIndex(p => p.id === product.id);
      
      if (productIndex !== -1) {
        products[productIndex] = product;
        localStorage.setItem('products', JSON.stringify(products));
        localStorage.setItem('stockpro_products', JSON.stringify(products));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving product to localStorage', error);
      return false;
    }
  }
  
  /**
   * Check for inconsistencies between stock movements and product stock
   * @returns {Object} Results of check
   */
  checkStockMovementConsistency() {
    try {
      console.group('Stock Movement Consistency Check');
      
      const products = this._loadProducts();
      const stockMovements = this._loadStockMovements();
      const results = {
        totalProducts: products.length,
        inconsistentProducts: 0,
        issues: []
      };
      
      // Group movements by product
      const movementsByProduct = {};
      for (const movement of stockMovements) {
        if (movement.productId) {
          if (!movementsByProduct[movement.productId]) {
            movementsByProduct[movement.productId] = [];
          }
          movementsByProduct[movement.productId].push(movement);
        }
      }
      
      // Check each product
      for (const product of products) {
        const productMovements = movementsByProduct[product.id] || [];
        
        // Calculate expected stock from movements
        let expectedStock = 0;
        for (const movement of productMovements) {
          if (movement.type === 'entrée') {
            expectedStock += parseInt(movement.quantity || 0);
          } else if (movement.type === 'sortie') {
            expectedStock -= parseInt(movement.quantity || 0);
          } else if (movement.type === 'ajustement') {
            // Adjustment sets the stock directly
            expectedStock = parseInt(movement.quantity || 0);
          }
        }
        
        expectedStock = Math.max(0, expectedStock);
        
        // Compare with actual stock
        if (parseInt(product.stock || 0) !== expectedStock && productMovements.length > 0) {
          results.inconsistentProducts++;
          results.issues.push(
            `Product ${product.id} (${product.name}): Stock mismatch. Actual: ${product.stock}, Expected: ${expectedStock}`
          );
        }
      }
      
      console.log('Stock Movement Consistency Results:', results);
      return results;
    } catch (error) {
      console.error('Error checking stock movement consistency:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }
  
  /**
   * Load stock movements from localStorage
   * @returns {Array} Stock movements array
   */
  _loadStockMovements() {
    try {
      const stockItems = localStorage.getItem('stockpro_stock') ? 
        JSON.parse(localStorage.getItem('stockpro_stock')) : [];
      return stockItems;
    } catch (error) {
      console.error('Error loading stock movements from localStorage', error);
      return [];
    }
  }
}

// Create and export a singleton instance
const dataIntegrityService = new DataIntegrityService();
export default dataIntegrityService;